




























































import { Component, Vue } from "vue-property-decorator";
import { activityLogApiProvider } from "@/providers/activity-log-api-provider";
import { DxExport, DxFieldChooser, DxPivotGrid, DxStateStoring } from "devextreme-vue/pivot-grid";
import { loadMessages, locale } from "devextreme/localization";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import frMessages from "devextreme/localization/messages/fr.json";
import CustomStore from "devextreme/data/custom_store";
import { addMonths, subMonths, startOfMonth, endOfMonth, format } from "date-fns";
import { fr, enGB } from "date-fns/locale";
import store from "@/stores/store";

// DevExtreme localization
locale(navigator.language);
loadMessages(frMessages);
@Component({
  name: "stats-page",
  components: {
    DxPivotGrid,
    DxFieldChooser,
    DxExport,
    DxStateStoring,
  },
})
export default class ParamsPage extends Vue {
  private stats = null;
  private statsJson = null;
  private loading = false;
  private needRefresh = false;

  private startDate: Date = subMonths(startOfMonth(new Date()), 2);
  private endDate: Date = endOfMonth(new Date());

  /**
   * Lance (ou relance) le chargement des statistiques.
   * Le chargement n'est jamais automatique : la navigation entre les mois ne
   * fait que changer la période, l'utilisateur déclenche le calcul lui-même.
   */
  async loadStats() {
    if (this.loading) {
      return;
    }
    this.loading = true;

    if (!this.stats) {
      // Le premier clic crée la source de données : le pivot se monte et
      // déclenche lui-même le chargement via le CustomStore.
      this.stats = this.buildDataSource();
      return;
    }

    try {
      await this.stats.reload();
    } catch (e) {
      console.error(e);
      this.loading = false;
    }
  }

  private buildDataSource() {
    const dataSource = new PivotGridDataSource({
      store: new CustomStore({
        key: "id",
        load: async (loadOptions) => {
          const params = new URLSearchParams();
          params.append('filter[created_at][ge]', format(this.startDate, 'yyyy-MM-dd'))
          params.append('filter[created_at][le]', format(this.endDate, 'yyyy-MM-dd'))

          try {
            const response = await activityLogApiProvider.getData(params);
            this.statsJson = response;
            return response;
          } catch (e) {
            console.error(e);
            this.statsJson = null;
            return [];
          } finally {
            this.needRefresh = false;
          }
        }
      }),
      fields: [
        {
          caption: "Code compagnie",
          dataField: "user.company.code",
          area: "row",
        },
        {
          caption: "Nombre",
          dataField: "id",
          area: "data",
        },
        {
          caption: "Type",
          width: 150,
          dataField: "type",
          area: "row",
        },
        {
          caption: "Page",
          dataField: "pageName",
          width: 150,
          area: "row",
        },
        {
          dataField: "createdAt",
          dataType: "date",
          area: "column",
        },
      ]
    });

    // Le spinner reste actif jusqu'à la fin du calcul du pivot (et pas
    // seulement de la requête API), puis se coupe en cas d'erreur.
    dataSource.on("loadingChanged", (isLoading: boolean) => {
      this.loading = isLoading;
    });
    dataSource.on("loadError", () => {
      this.loading = false;
    });

    return dataSource;
  }

  formatDateDisplay(date: Date) {
    return format(date, "MMMM yyyy", { locale:  store.getters["user/getLocale"] === 'fr' ? fr : enGB });
  }

  changePeriod(direction: number) {
    this.startDate = addMonths(this.startDate, direction);
    this.endDate = endOfMonth(addMonths(this.endDate, direction));

    this.needRefresh = true;
  }

  resetToDefault() {
    this.startDate = subMonths(startOfMonth(new Date()), 2);
    this.endDate = endOfMonth(new Date());

    this.needRefresh = true;
  }

  jsonToCsv(jsonArray) {
    // Création de l'en-tête CSV
    const header = this.$t("stats.csv_header").toString();

    // Fonction pour formater une date en format ISO 8601
    function formatDate(dateString) {
      if (!dateString) {
        return "";
      }
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString();
    }

    // Fonction pour formater une ligne CSV à partir d'un objet JSON.
    // L'utilisateur (et sa société) peuvent être absents : on sort des
    // colonnes vides plutôt que de casser l'export complet.
    function formatCsvRow(item) {
      const { type, pageName, createdAt, user } = item ?? {};
      const date = formatDate(createdAt);
      return [
        type,
        pageName,
        date,
        user?.company?.name,
        user?.lastname,
        user?.firstname,
        user?.email,
      ]
        .map((value) => value ?? "")
        .join(";");
    }

    // Construction du CSV à partir du tableau JSON
    const csvArray = (jsonArray ?? []).map((item) => formatCsvRow(item));
    const csvRows = csvArray.join("\r\n");

    // Ajout de l'entête UTF-8 avec des caractères spéciaux pour assurer leur affichage correct
    const headerUtf8 = "\uFEFF" + header + "\r\n";
    return headerUtf8 + csvRows;
  }

  downloadCsv(filename) {
    const csv = this.jsonToCsv(this.statsJson);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}
